import { useForm } from '@tanstack/react-form'
import { getRouteApi } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import type { UserResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import { DataTable, RowActionsMenu } from '@trellis/roots/data'
import { SearchInput, EmailInput } from '@trellis/roots/forms'
import { Label } from '@trellis/roots/label'
import { Avatar } from '@trellis/roots/media'
import { TabBar } from '@trellis/roots/navigation'
import { FormDialog } from '@trellis/roots/overlays'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import { InviteUserSchema } from '@trellis/shared'
import type { UserRole } from '@trellis/shared'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Crown,
  KeyRound,
  ShieldCheck,
  User as UserIcon,
  UserCheck,
  UserCog,
  Users,
  UserX,
  PlusIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { EmailDivertNotice } from '@/components/email'
import { FilterSheet } from '@/components/shell'
import { useDisclosure, usePinnedColumn, useTableSorting } from '@/hooks'
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch'
import {
  useCancelInvitationMutation,
  useDeactivateUserMutation,
  useInviteUserMutation,
  usePendingInvitationsQuery,
  useReactivateUserMutation,
  useResetPasswordMutation,
  useUpdateUserRoleMutation,
  useUsersQuery,
  useUsersSummaryQuery,
} from '@/hooks/useUsers'
import { authClient } from '@/lib/auth-client'
import { useSetBreadcrumbs } from '@/lib/breadcrumbs'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'

export const ALL_USER_STATUSES = ['active', 'deactivated'] as const
type UserStatus = (typeof ALL_USER_STATUSES)[number]
type VerifiedFilter = 'verified' | 'unverified'

const STATUS_TABS: { key: UserStatus; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'deactivated', label: 'Deactivated' },
]

const usersSettingsRouteApi = getRouteApi('/_authenticated/_dashboard/settings/users/')

const ROLE_STYLES: Record<UserRole, { pill: string; dot: string }> = {
  OWNER: { pill: 'bg-violet-50 text-violet-700', dot: 'bg-violet-500' },
  ADMIN: { pill: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
  MEMBER: { pill: 'bg-stone-100 text-stone-600', dot: 'bg-stone-400' },
}

const ROLE_LABELS: Record<UserRole, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  MEMBER: 'Member',
}

const ROLE_ICONS: Record<UserRole, LucideIcon> = {
  OWNER: Crown,
  ADMIN: ShieldCheck,
  MEMBER: UserIcon,
}

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  OWNER: 'Full control, including billing and deleting the workspace',
  ADMIN: 'Manages users and workspace settings',
  MEMBER: 'Everyday access to clients, invoices, and quotes',
}

const ROLE_ORDER: UserRole[] = ['OWNER', 'ADMIN', 'MEMBER']

function RoleBadge({ role }: { role: UserRole }) {
  const { pill, dot } = ROLE_STYLES[role]
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${pill}`}
    >
      <span className={`size-1.5 shrink-0 rounded-full ${dot}`} />
      {ROLE_LABELS[role]}
    </span>
  )
}

function UnverifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
      <span className="size-1.5 shrink-0 rounded-full bg-amber-500" />
      Unverified
    </span>
  )
}

function VerifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
      <span className="size-1.5 shrink-0 rounded-full bg-green-500" />
      Verified
    </span>
  )
}

function InviteUserDialog({ atLimit }: { atLimit: boolean }) {
  const [open, setOpen] = useState(false)
  const { mutateAsync: invite } = useInviteUserMutation()

  const form = useForm({
    defaultValues: { email: '', role: 'MEMBER' as 'ADMIN' | 'MEMBER' },
    validators: { onSubmit: InviteUserSchema },
    onSubmit: async ({ value }) => {
      try {
        const result = await invite(value)
        if (result.error) {
          toast.error('Failed to send invitation', { description: result.error.message })
          return
        }
        toast.success('Invitation sent', { description: `Invited ${value.email}` })
        setOpen(false)
        form.reset()
      } catch {
        toast.error('Failed to send invitation')
      }
    },
  })

  if (atLimit) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button disabled>
              <PlusIcon />
              Invite user
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          User limit reached. Deactivate a user or cancel a pending invitation to invite
          someone new.
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <PlusIcon />
        Invite user
      </Button>
      <form.Subscribe selector={(state) => state.isSubmitting}>
        {(isSubmitting) => (
          <FormDialog
            open={open}
            onOpenChange={setOpen}
            title="Invite a user"
            description="Invite a new user by email."
            hideDescription
            submitLabel="Send invitation"
            isPending={isSubmitting}
            onSubmit={() => void form.handleSubmit()}
          >
            <form.Field name="email">
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="invite-email">Email address</Label>
                  <EmailInput
                    id="invite-email"
                    placeholder="colleague@example.com"
                    value={field.state.value}
                    onChange={(changeEvent) =>
                      field.handleChange(changeEvent.target.value)
                    }
                    onBlur={field.handleBlur}
                  />
                  {field.state.meta.errors[0] && (
                    <p className="text-destructive-text text-xs">
                      {field.state.meta.errors[0].message}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="role">
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="invite-role">Role</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) =>
                      field.handleChange(value as 'ADMIN' | 'MEMBER')
                    }
                  >
                    <SelectTrigger id="invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ADMIN">Admin</SelectItem>
                      <SelectItem value="MEMBER">Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <EmailDivertNotice />
          </FormDialog>
        )}
      </form.Subscribe>
    </>
  )
}

function UpdateRoleDialog({
  user,
  onOpenChange,
}: {
  user: UserResponseDto | null
  onOpenChange: (open: boolean) => void
}) {
  const { mutateAsync: updateRole, isPending } = useUpdateUserRoleMutation()
  const [selectedRole, setSelectedRole] = useState<UserRole | undefined>(user?.role)

  useEffect(() => {
    setSelectedRole(user?.role)
  }, [user])

  async function handleSave() {
    if (!user || !selectedRole || selectedRole === user.role) return
    const result = await updateRole({ userId: user.id, data: { role: selectedRole } })
    if (result.error) {
      toast.error('Failed to update role', { description: result.error.message })
      return
    }
    toast.success('Role updated')
    onOpenChange(false)
  }

  return (
    <FormDialog
      open={user !== null}
      onOpenChange={onOpenChange}
      title="Update role"
      description={`Choose what ${user?.name} can do in this workspace.`}
      submitLabel="Save"
      isPending={isPending}
      submitDisabled={!selectedRole || selectedRole === user?.role}
      onSubmit={() => void handleSave()}
    >
      <div className="flex flex-col gap-2 pt-2">
        {ROLE_ORDER.map((role) => {
          const Icon = ROLE_ICONS[role]
          const isCurrent = user?.role === role
          const isSelected = selectedRole === role
          return (
            <button
              key={role}
              type="button"
              disabled={isPending}
              onClick={() => setSelectedRole(role)}
              aria-pressed={isSelected}
              className={cn(
                'flex items-start gap-3.5 rounded-xl border p-4 text-left transition-colors',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'hover:border-foreground/30 hover:bg-muted/20',
                isPending && 'pointer-events-none opacity-60',
              )}
            >
              <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-[10px]">
                <Icon size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{ROLE_LABELS[role]}</span>
                  {isCurrent && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
                      <span className="size-1.5 shrink-0 rounded-full bg-emerald-600" />
                      Current
                    </span>
                  )}
                </div>
                <div className="text-muted-foreground mt-1 text-[13px] leading-snug">
                  {ROLE_DESCRIPTIONS[role]}
                </div>
              </div>
              {isSelected && <Check className="text-primary mt-0.5 size-4 shrink-0" />}
            </button>
          )
        })}
      </div>
    </FormDialog>
  )
}

function buildUserColumns(
  onResetPassword: (user: UserResponseDto) => void,
  onDeactivate: (user: UserResponseDto) => void,
  onReactivate: (user: UserResponseDto) => void,
  onUpdateRole: (user: UserResponseDto) => void,
  currentUserId: string | undefined,
): ColumnDef<UserResponseDto>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      enableSorting: true,
      meta: { truncate: true },
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar name={row.original.name} size="sm" />
          <span>{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      enableSorting: true,
      meta: { truncate: true },
    },
    {
      id: 'role',
      header: 'Role',
      enableSorting: false,
      cell: ({ row }) => <RoleBadge role={row.original.role} />,
    },
    {
      id: 'verification',
      header: 'Verification',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.emailVerified ? <VerifiedBadge /> : <UnverifiedBadge />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      enableSorting: true,
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) => {
        const user = row.original
        const isSelf = user.id === currentUserId
        return (
          <RowActionsMenu
            triggerLabel={`Open actions menu for ${user.name}`}
            actions={
              user.isActive
                ? [
                    {
                      label: 'Update role',
                      icon: <UserCog className="mr-2 h-4 w-4" />,
                      onClick: () => onUpdateRole(user),
                      disabled: isSelf,
                    },
                    {
                      label: 'Reset password',
                      icon: <KeyRound className="mr-2 h-4 w-4" />,
                      onClick: () => onResetPassword(user),
                    },
                    {
                      label: 'Deactivate',
                      icon: <UserX className="mr-2 h-4 w-4" />,
                      onClick: () => onDeactivate(user),
                      destructive: true,
                      separator: true,
                      disabled: isSelf,
                    },
                  ]
                : [
                    {
                      label: 'Update role',
                      icon: <UserCog className="mr-2 h-4 w-4" />,
                      onClick: () => onUpdateRole(user),
                      disabled: isSelf,
                    },
                    {
                      label: 'Reactivate',
                      icon: <UserCheck className="mr-2 h-4 w-4" />,
                      onClick: () => onReactivate(user),
                    },
                  ]
            }
          />
        )
      },
    },
  ]
}

export function UsersSettingsPage() {
  useSetBreadcrumbs([{ label: 'Settings', href: '/settings' }, { label: 'Users' }])

  const { data: session } = authClient.useSession()
  const { inputValue, debouncedValue, setInputValue } = useDebouncedSearch()
  const { sorting, onSortingChange } = useTableSorting('users-sorting')
  const { pinnedColumnId, onPinnedColumnChange } = usePinnedColumn('users-pinned-column')
  const { status: statusFilter } = usersSettingsRouteApi.useSearch()
  const navigateSearch = usersSettingsRouteApi.useNavigate()
  const [roleFilter, setRoleFilter] = useState<UserRole | undefined>(undefined)
  const [verifiedFilter, setVerifiedFilter] = useState<VerifiedFilter | undefined>(
    undefined,
  )
  const roleDialog = useDisclosure<UserResponseDto>()

  const status = statusFilter ?? 'active'

  const {
    isFetching,
    pageIndex,
    setPageIndex,
    currentPageData,
    totalPages,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
  } = useUsersQuery({
    search: debouncedValue || undefined,
    status,
    role: roleFilter,
    verified: verifiedFilter,
    limit: 20,
  })
  const { data: summaryResult, isLoading: summaryLoading } = useUsersSummaryQuery()
  const { data: pendingInvitationsResult } = usePendingInvitationsQuery()
  const { mutateAsync: deactivate } = useDeactivateUserMutation()
  const { mutateAsync: reactivate } = useReactivateUserMutation()
  const { mutateAsync: resetPassword } = useResetPasswordMutation()
  const { mutateAsync: cancelInvite } = useCancelInvitationMutation()

  useEffect(() => {
    setPageIndex(0)
  }, [status, debouncedValue, roleFilter, verifiedFilter, setPageIndex])

  const rows = currentPageData?.data ?? []
  const pendingInvitations = pendingInvitationsResult?.data ?? []
  const summary = summaryResult?.data
  /* Mirrors inviteUser's limit check, which counts active members + pending invitations. */
  const occupiedSeats =
    summary != null ? summary.active + pendingInvitations.length : null

  async function handleDeactivate(user: UserResponseDto) {
    const result = await deactivate(user.id)
    if (result.error) {
      toast.error('Failed to deactivate user', { description: result.error.message })
    } else {
      toast.success(`${user.name} has been deactivated`)
    }
  }

  async function handleReactivate(user: UserResponseDto) {
    const result = await reactivate(user.id)
    if (result.error) {
      toast.error('Failed to reactivate user', { description: result.error.message })
    } else {
      toast.success(`${user.name} has been reactivated`)
    }
  }

  async function handleResetPassword(user: UserResponseDto) {
    const result = await resetPassword(user.id)
    if (result.error) {
      toast.error('Failed to send reset-password email', {
        description: result.error.message,
      })
    } else {
      toast.success(`Password reset email sent to ${user.name}`)
    }
  }

  async function handleCancelInvitation(id: string, email: string) {
    const result = await cancelInvite(id)
    if (result.error) {
      toast.error('Failed to cancel invitation', { description: result.error.message })
    } else {
      toast.success(`Invitation to ${email} cancelled`)
    }
  }

  const userColumns = buildUserColumns(
    handleResetPassword,
    handleDeactivate,
    handleReactivate,
    roleDialog.open,
    session?.user.id,
  )

  return (
    <div className="p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="bg-foreground text-background flex size-11 shrink-0 items-center justify-center rounded-[12px]">
            <Users size={18} />
          </div>
          <div>
            <Typography variant="overline" color="muted">
              Settings · Workspace
            </Typography>
            <Typography variant="display-lg" as="h1" className="mt-0.5">
              Users
            </Typography>
            <Typography variant="body-sm" color="muted" className="mt-1">
              Manage users and pending invitations.
            </Typography>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {summary && occupiedSeats != null && (
            <Typography variant="label" color="muted">
              {occupiedSeats} / {summary.limit} members
            </Typography>
          )}
          <InviteUserDialog
            atLimit={
              summary != null && occupiedSeats != null && occupiedSeats >= summary.limit
            }
          />
        </div>
      </div>

      {/* Status tabs */}
      <TabBar
        tabs={STATUS_TABS.map(({ key, label }) => ({
          key,
          label,
          count: summary?.[key] ?? 0,
        }))}
        activeKey={status}
        onChange={(key) =>
          void navigateSearch({
            search: (prev) => ({
              ...prev,
              status: key === 'active' ? undefined : (key as UserStatus),
            }),
          })
        }
        isLoading={summaryLoading}
      />

      {/* Toolbar */}
      <div className="mt-3 mb-3">
        <FilterSheet
          activeCount={(roleFilter ? 1 : 0) + (verifiedFilter ? 1 : 0)}
          onClear={() => {
            setRoleFilter(undefined)
            setVerifiedFilter(undefined)
          }}
          toolbar={
            <>
              <Select
                value={roleFilter ?? '__all__'}
                onValueChange={(value) =>
                  setRoleFilter(value === '__all__' ? undefined : (value as UserRole))
                }
              >
                <SelectTrigger aria-label="Role" className="h-9 w-[160px]">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" className="pl-6">
                    All roles
                  </SelectItem>
                  <SelectItem value="OWNER" className="pl-6">
                    Owner
                  </SelectItem>
                  <SelectItem value="ADMIN" className="pl-6">
                    Admin
                  </SelectItem>
                  <SelectItem value="MEMBER" className="pl-6">
                    Member
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={verifiedFilter ?? '__all__'}
                onValueChange={(value) =>
                  setVerifiedFilter(
                    value === '__all__' ? undefined : (value as VerifiedFilter),
                  )
                }
              >
                <SelectTrigger aria-label="Verification status" className="h-9 w-45">
                  <SelectValue placeholder="All status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" className="pl-6">
                    All status
                  </SelectItem>
                  <SelectItem value="verified" className="pl-6">
                    Verified
                  </SelectItem>
                  <SelectItem value="unverified" className="pl-6">
                    Unverified
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="flex-1" />
              <SearchInput
                placeholder="Search users"
                value={inputValue}
                onChange={(changeEvent) => setInputValue(changeEvent.target.value)}
                className="max-w-sm"
              />
            </>
          }
          sheet={
            <>
              <Select
                value={roleFilter ?? '__all__'}
                onValueChange={(value) =>
                  setRoleFilter(value === '__all__' ? undefined : (value as UserRole))
                }
              >
                <SelectTrigger aria-label="Role">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" className="pl-6">
                    All roles
                  </SelectItem>
                  <SelectItem value="OWNER" className="pl-6">
                    Owner
                  </SelectItem>
                  <SelectItem value="ADMIN" className="pl-6">
                    Admin
                  </SelectItem>
                  <SelectItem value="MEMBER" className="pl-6">
                    Member
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={verifiedFilter ?? '__all__'}
                onValueChange={(value) =>
                  setVerifiedFilter(
                    value === '__all__' ? undefined : (value as VerifiedFilter),
                  )
                }
              >
                <SelectTrigger aria-label="Verification status">
                  <SelectValue placeholder="All status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" className="pl-6">
                    All status
                  </SelectItem>
                  <SelectItem value="verified" className="pl-6">
                    Verified
                  </SelectItem>
                  <SelectItem value="unverified" className="pl-6">
                    Unverified
                  </SelectItem>
                </SelectContent>
              </Select>
            </>
          }
        />
      </div>

      {/* Table */}
      <DataTable
        columns={userColumns}
        data={rows}
        isLoading={isFetching && rows.length === 0}
        emptyMessage="No users found."
        sorting={sorting}
        onSortingChange={onSortingChange}
        enableColumnPinning
        pinnedColumnId={pinnedColumnId}
        onPinnedColumnChange={onPinnedColumnChange}
        className="rounded-b-none"
        getRowId={(row) => row.id}
      />
      {/* Pagination footer */}
      <div className="bg-muted/30 text-muted-foreground mt-0 flex items-center justify-between rounded-b-lg border-x border-b px-4 py-3 text-[12.5px]">
        <span>
          Page {pageIndex + 1} of {totalPages}
          {hasNextPage ? '+' : ''}
        </span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={pageIndex === 0}
            onClick={goToPrevPage}
            className="h-7 text-xs"
          >
            <ChevronLeft className="mr-1 size-3.5" />
            Prev
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasNextPage || isFetching}
            onClick={goToNextPage}
            className="h-7 text-xs"
          >
            Next
            <ChevronRight className="ml-1 size-3.5" />
          </Button>
        </div>
      </div>

      {/* Pending invitations section */}
      {pendingInvitations.length > 0 && (
        <section className="mt-8">
          <Typography variant="h6" as="h2" className="mb-3">
            Pending invitations
          </Typography>
          <ul className="divide-y rounded-lg border">
            {pendingInvitations.map((invitation) => (
              <li
                key={invitation.id}
                className="flex items-center justify-between px-4 py-3"
              >
                <div className="min-w-0">
                  <Typography variant="body" className="truncate">
                    {invitation.email}
                  </Typography>
                  <Typography variant="label" color="muted">
                    Expires{' '}
                    {formatDate(invitation.expiresAt, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Typography>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <RoleBadge role={invitation.role} />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive-text hover:text-destructive-text text-xs"
                    onClick={() =>
                      handleCancelInvitation(invitation.id, invitation.email)
                    }
                  >
                    Cancel
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <UpdateRoleDialog user={roleDialog.value} onOpenChange={roleDialog.onOpenChange} />
    </div>
  )
}
